import React from 'react';
import PropTypes from 'prop-types';
import ReactEcharts from 'echarts-for-react';
import merge from 'lodash.merge';

import { traitPropType, stylePropType } from 'src/PropTypes';
import { Style1 } from 'src/styles';
import themes from 'src/themes';

/* eslint-disable no-nested-ternary */
const TOTAL_COLORS = 22;

function Slider({ title, label: trait, series, style, options, onClick, theme, min: minProp, max: maxProp }) {
	if (!trait || !series) {
		return <div>Loading</div>;
	}
	const max = Math.max(50, maxProp);
	const min = Math.min(50, minProp);
	const x = [];
	const data1 = [];
	for (let i = min; i < max; i += 0.1) {
		x.push(i);
		data1.push(1);
	}
	// due to floating point errors we need to
	// make sure the values the boundary values are
	// pushed
	x.push(max);
	data1.push(1);
	const totalRange = max - min;
	const below50Range = 50 - min;
	const below50TotalColors = (below50Range * TOTAL_COLORS) / totalRange;
	const above50TotalColors = TOTAL_COLORS - below50TotalColors;
	const areaSeries = [];
	for (let i = 0; i < series.length; i += 1) {
		areaSeries.push({
			type: 'scatter',
			name: series[i].name,
			data: [[series[i].value.toFixed(1), 0.5]],
			label: {
				...style.Slider.Scatter.label,
				formatter: () => series[i].value.toFixed(1),
			},
			itemStyle: {
				...style.Slider.Scatter.itemStyle,
				color: series[i].theme && themes[series[i].theme] ? themes[series[i].theme].color[0] : undefined,
			},
			...style.Slider.Scatter.series,
		});
	}
	const chartOption = {
		title: {
			text: title,
			...style.global.title,
		},
		grid: {
			...style.Slider.grid,
		},
		textStyle: {
			...style.Slider.textStyle,
		},
		legend: {
			...style.Slider.legend,
			show: series.length > 1,
			top: 'bottom',
		},
		backgroundColor: style.global.backgroundColor,
		xAxis: [
			{
				data: x.map((val) => val.toFixed(1)),
				name: 'Median Score',
				nameGap: 0,
				...style.Slider.xAxis,
				axisLabel: {
					show: false,
					...style.Slider.xAxis.axisLabel,
				},
				splitLine: {
					show: false,
				},
				axisLine: {
					show: false,
				},
				axisTick: {
					show: false,
				},
			},
		],
		yAxis: [
			{
				axisLabel: {
					show: false,
					...style.Slider.yAxis.axisLabel,
				},
				splitLine: {
					show: false,
				},
				axisLine: {
					show: false,
				},
				axisTick: {
					show: false,
				},
			},
			{
				data: [trait.lowRangeName],
				axisLabel: {
					show: true,
					...style.Slider.yAxis.axisLabel,
				},
				splitLine: {
					show: false,
				},
				axisLine: {
					show: false,
				},
				axisTick: {
					show: false,
				},
				position: 'left',
			},
			{
				data: [trait.highRangeName],
				axisLabel: {
					show: true,
					...style.Slider.yAxis.axisLabel,
				},
				splitLine: {
					show: false,
				},
				axisLine: {
					show: false,
				},
				axisTick: {
					show: false,
				},
			},
		],
		series: [
			...areaSeries,
			{
				type: 'line',
				symbol: 'none',
				data: data1,
				lineStyle: style.Slider.lineStyle,
				smooth: true,
				areaStyle: {
					color: {
						type: 'linear',
						x: 1,
						y: 0,
						x2: 0,
						y2: 0,
						colorStops: style.COLORS.generateColorStops(
							below50TotalColors,
							above50TotalColors,
							style.COLORS.GRADIENTS.ELECTRIC_BLUE_RED
						),
					},
				},
				...style.Slider.series,
			},
		],
	};
	const mergedOptions = merge(chartOption, options);
	return (
		<div
			style={{
				width: '100%',
				height: '120px',
			}}
		>
			<ReactEcharts
				ref={(element) => {
					const instance = element ? element.getEchartsInstance() : null;
					if (instance) {
						instance.off('click');
						instance.on('click', 'series', (params) => {
							onClick(params.data.code);
						});
					}
				}}
				style={{
					width: '100%',
					height: '100%',
				}}
				option={mergedOptions}
				theme={theme}
			/>
		</div>
	);
}

Slider.propTypes = {
	label: traitPropType.isRequired,
	series: PropTypes.arrayOf(
		PropTypes.shape({
			name: PropTypes.string,
			value: PropTypes.number,
		})
	).isRequired,
	title: PropTypes.string,
	style: stylePropType,
	options: PropTypes.shape({}),
	theme: PropTypes.string,
	onClick: PropTypes.func,
	min: PropTypes.number,
	max: PropTypes.number,
};

Slider.defaultProps = {
	style: Style1,
	title: '',
	options: {},
	onClick: () => null,
	theme: 'pinpoint',
	min: 0,
	max: 100,
};

export default Slider;
